'use client';

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { NotificationsOutlined } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Session } from 'next-auth';

type Notification = {
  id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  link: string;
  createdAt: string;
};
function NotificationBellInner() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [shouldRefresh, setShouldRefresh] = useState(true);
  const router = useRouter();
  const queryClient = useQueryClient();

  // Only fetch notifications if user is authenticated
  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () =>
      fetch('/api/notifications')
        .then(response => response.json())
        .catch(response => {
          console.error('Failed to fetch notifications', response.status);
          setShouldRefresh(false);
          return null;
        }),
    refetchInterval: 60_000, // Refetch every 60 seconds
    enabled: shouldRefresh,
  });

  // Mark notifications as read
  const markAsRead = useMutation({
    mutationFn: async (notificationIds: string[]) =>
      fetch('/api/notifications/mark-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notificationIds }),
      })
        .then(response => response.json())
        .catch(response => {
          console.error('Failed to mark notifications as read', response.status);
          return null;
        }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      await markAsRead.mutateAsync([notification.id]);
    }
    handleClose();
    router.push(notification.link);
  };

  const notifications = data?.notifications || [];
  const unreadCount = data?.unreadCount || 0;

  return (
    <>
      <IconButton color="inherit" onClick={handleClick} sx={{ position: 'relative' }}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsOutlined />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
            mt: 1.5,
            minWidth: 320,
            maxHeight: 400,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </MenuItem>
        ) : (
          notifications.map((notification: Notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                py: 1.5,
                px: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: notification.read ? 'transparent' : 'action.hover',
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle2" gutterBottom>
                  {notification.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {notification.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDistanceToNow(new Date(notification.createdAt), {
                    addSuffix: true,
                  })}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
const queryClient = new QueryClient();

export function NotificationBell() {
  return (
    <QueryClientProvider client={queryClient}>
      <NotificationBellInner />
    </QueryClientProvider>
  );
}
